import { useState, useEffect, useRef } from 'react';
import { API_BASE, resolveAssetUrl } from '../../lib/rpc';
import { getCachedBranding } from '../../lib/branding';
import { crmClient, crmFetch } from '../../lib/crmRpc';
import { useToast } from '../ui/ToastProvider';
import { reportDataFetchError } from '../../lib/telemetry';
import { CrmDashboardData, ActiveAgent } from './crmUtils';
import { useCrmWebPush } from './useCrmWebPush';
import { useCrmChatActions } from './useCrmChatActions';

export function useCrmDashboard(slug?: string) {
  const { showToast } = useToast();
  const [data, setData] = useState<CrmDashboardData | null>(() => {
    if (!slug) return null;
    try {
      const agentName = localStorage.getItem(`crm-agent-${slug}`);
      if (!agentName) return null;
      const cached = localStorage.getItem(`crm-cache-${slug}`);
      if (!cached) return null;
      const parsed = JSON.parse(cached);
      if (parsed && typeof parsed === 'object' && parsed.crm) {
        return parsed;
      }
    } catch {
      return null;
    }
    return null;
  });
  const [error, setError] = useState<string | null>(null);
  const [clientTheme, setClientTheme] = useState<string | null>(() => {
    try { return localStorage.getItem('crm-theme'); } catch { return null; }
  });
  const [requiresAuth, setRequiresAuth] = useState(false);
  const [crmRequiresPassword, setCrmRequiresPassword] = useState(true);
  const [authInput, setAuthInput] = useState('');
  const [authName, setAuthName] = useState('');
  const [authError, setAuthError] = useState('');
  const [crmName, setCrmName] = useState<string | null>(() => {
    if (!slug) return null;
    try {
      const cached = localStorage.getItem(`crm-cache-${slug}`);
      if (!cached) return null;
      const parsed = JSON.parse(cached);
      return parsed?.crm?.name || null;
    } catch {
      return null;
    }
  });
  const [page, setPage] = useState(0);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState<'personal' | 'general' | 'security' | 'users'>('personal');
  const [clientMaxRows, setClientMaxRows] = useState<number | null>(() => { try { const v = localStorage.getItem('crm-max-rows'); return v ? parseInt(v) : null; } catch { return null; } });
  const [clientHotCallWindow, setClientHotCallWindow] = useState<number | null>(() => { try { const v = localStorage.getItem('crm-hot-window'); return v ? parseInt(v) : null; } catch { return null; } });
  const [clientHotCallToastsEnabled, setClientHotCallToastsEnabled] = useState<boolean>(() => { try { return localStorage.getItem('crm-hot-call-toasts') !== 'false'; } catch { return true; } });
  const [clientTimezone, setClientTimezone] = useState<string>(() => { try { return localStorage.getItem('crm-tz') || 'local'; } catch { return 'local'; } });

  const scrollRef = useRef<HTMLTableSectionElement>(null);
  const idleSinceRef = useRef<number>(Date.now());
  const notifiedLogIds = useRef<Set<string>>(new Set());
  const [toastQueue, setToastQueue] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'logs' | 'chat' | 'cdr' | 'voicemails'>('logs');
  const [unreadMessages, setUnreadMessages] = useState(0);

  const [cdrAuthed, setCdrAuthed] = useState(false);
  const [cdrPasswordInput, setCdrPasswordInput] = useState('');
  const [cdrAuthError, setCdrAuthError] = useState('');

  const [chatTrigger, setChatTrigger] = useState<{ contact: string; did?: string; ts: number; autoLookup?: boolean } | null>(null);
  const [showHeaderMobile, setShowHeaderMobile] = useState(false);
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  const [showLargeQr, setShowLargeQr] = useState(false);
  const [contactEditorModalOpen, setContactEditorModalOpen] = useState<{ open: boolean; contactId?: string; defaultData?: any; initialPhoneNumber?: string }>({ open: false });
  const [addressBookModalOpen, setAddressBookModalOpen] = useState(false);
  const [callLogsSettingsOpen, setCallLogsSettingsOpen] = useState(false);
  const [activeAgents, setActiveAgents] = useState<ActiveAgent[]>([]);
  const [timeSkew, setTimeSkew] = useState(0);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [cnamRefreshTrigger, setCnamRefreshTrigger] = useState(0);
  const [, setTick] = useState(0);

  // Sub-hooks for Web Push and Chat actions
  const webPush = useCrmWebPush({ slug, authInput, showToast });
  const chatActions = useCrmChatActions({ slug, authInput, data, setData });

  useEffect(() => {
    const int = setInterval(() => {
      setTick(t => t + 1);
      setNow(Date.now());
    }, 10000);
    return () => clearInterval(int);
  }, []);

  useEffect(() => {
    const handleActiveAgents = (e: Event) => {
      const ce = e as CustomEvent;
      if (ce.detail && Array.isArray(ce.detail.users)) {
        setActiveAgents(ce.detail.users);
        if (ce.detail.serverTime) setTimeSkew(Date.now() - ce.detail.serverTime);
      }
    };
    const handleMessageToast = (e: Event) => {
      const ce = e as CustomEvent;
      setToastQueue(prev => [...prev, { ...ce.detail, addedAt: Date.now() }]);
    };

    window.addEventListener('crm-active-agents', handleActiveAgents);
    window.addEventListener('crm-message-toast', handleMessageToast);

    return () => {
      window.removeEventListener('crm-active-agents', handleActiveAgents);
      window.removeEventListener('crm-message-toast', handleMessageToast);
    };
  }, []);

  useEffect(() => {
    let isAuthed = true;
    let ws: WebSocket | null = null;
    const pw = (slug && localStorage.getItem(`crm-pw-${slug}`)) || '';

    const connectWebSocket = () => {
      if (!slug) return;
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = API_BASE ? API_BASE.replace(/^https?:\/\//, '').replace(/\/$/, '') : window.location.host;
      const pwQuery = pw ? `?pw=${encodeURIComponent(pw)}` : '';
      const wsUrl = `${protocol}//${host}/api/v1/crm-dashboard/${slug}/ws${pwQuery}`;

      try {
        ws = new WebSocket(wsUrl);

        ws.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data);
            if (msg.type === 'refresh_cnam') {
              setCnamRefreshTrigger(p => p + 1);
              setRefreshTrigger(p => p + 1);
            } else if (msg.type === 'refresh') {
              setRefreshTrigger(p => p + 1);
            }
          } catch {
            if (event.data === 'refresh_cnam') {
              setCnamRefreshTrigger(p => p + 1);
              setRefreshTrigger(p => p + 1);
            } else if (event.data === 'refresh') {
              setRefreshTrigger(p => p + 1);
            }
          }
        };

        ws.onclose = () => {
          if (isAuthed) {
            setTimeout(connectWebSocket, 3000);
          }
        };
      } catch {}
    };

    connectWebSocket();

    return () => {
      isAuthed = false;
      if (ws) ws.close();
    };
  }, [slug]);

  const hotCallWindow = clientHotCallWindow !== null ? clientHotCallWindow : (data?.crm?.hotCallWindow || 0);
  const hotCallsCount = hotCallWindow > 0 ? (data?.logs || []).filter((log: any) => {
    const isHit = log.resolvedStatus === 'HIT' || log.resolvedStatus === 'MISS' || log.resolvedStatus === 'BYPASS';
    return isHit && (Math.floor(Date.now() / 1000) - log.createdAt) <= (hotCallWindow * 60);
  }).length : 0;

  useEffect(() => {
    const totalNotifications = unreadMessages + hotCallsCount;
    const baseName = data?.crm?.name || crmName || 'Dashboard';

    if (hotCallsCount > 0 && data?.logs) {
      const hotWindow = clientHotCallWindow !== null ? clientHotCallWindow : (data?.crm?.hotCallWindow || 0);
      const currentHotCalls = data.logs.filter((log: any) => {
        const isHit = log.resolvedStatus === 'HIT' || log.resolvedStatus === 'MISS' || log.resolvedStatus === 'BYPASS';
        return isHit && (Math.floor(Date.now() / 1000) - log.createdAt) <= (hotWindow * 60);
      });

      currentHotCalls.forEach((log: any) => {
        if (!notifiedLogIds.current.has(log.id)) {
          notifiedLogIds.current.add(log.id);

          if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
            let name = log.responsePayload || 'Unknown Caller';
            try {
              const p = JSON.parse(log.responsePayload);
              if (p.name) name = p.name;
            } catch {}

            let formattedPhone = log.targetNumber;
            let clean = formattedPhone.replace(/\D/g, '');
            if (clean.length === 11 && clean.startsWith('1')) clean = clean.slice(1);
            if (clean.length === 10) formattedPhone = `(${clean.slice(0, 3)}) ${clean.slice(3, 6)}-${clean.slice(6)}`;

            const b = getCachedBranding();
            const notifIcon = b?.BRANDING_FAVICON
              ? resolveAssetUrl(b.BRANDING_FAVICON)
              : (b?.BRANDING_LOGO ? resolveAssetUrl(b.BRANDING_LOGO) : '/icon-192.png');

            if ('serviceWorker' in navigator) {
              navigator.serviceWorker.ready.then(reg => {
                reg.showNotification('Incoming Call', {
                  body: `${name}\n${formattedPhone}`,
                  icon: notifIcon,
                  badge: '/badge-96.png',
                  vibrate: [200, 100, 200]
                } as any).catch(() => {});
              });
            } else {
              try {
                const n = new Notification('Incoming Call', {
                  body: `${name}\n${formattedPhone}`,
                  icon: notifIcon
                });
                n.onclick = () => { window.focus(); n.close(); };
              } catch (e) {
                console.error("Native notification failed:", e);
              }
            }
          }

          if (clientHotCallToastsEnabled) {
            setToastQueue(prev => [...prev, { ...log, addedAt: Date.now() }]);
          }
        }
      });
    }

    const syncBadges = (count: number) => {
      if (typeof navigator !== 'undefined' && 'setAppBadge' in navigator) {
        if (count > 0) {
          navigator.setAppBadge(count).catch(() => {});
        } else {
          navigator.clearAppBadge().catch(() => {});
        }
      }

      try {
        if (typeof window !== 'undefined') {
          if ((window as any).Ferdium?.setBadge) {
            (window as any).Ferdium.setBadge(count, 0);
          } else if ((window as any).Ferdi?.setBadge) {
            (window as any).Ferdi.setBadge(count, 0);
          } else if ((window as any).Franz?.setBadge) {
            (window as any).Franz.setBadge(count, 0);
          }
        }
      } catch {}
    };

    syncBadges(totalNotifications);

    if (totalNotifications > 0) {
      let toggle = true;
      document.title = `(${totalNotifications}) 🔴 ${baseName}`;

      const interval = setInterval(() => {
        toggle = !toggle;
        let alertText = `New Activity`;
        if (unreadMessages > 0 && hotCallsCount === 0) alertText = `New Message`;
        if (unreadMessages === 0 && hotCallsCount > 0) alertText = `Hot Caller`;

        document.title = `(${totalNotifications}) ${toggle ? '🔴' : '🔔'} ${alertText} - ${baseName}`;
      }, 1500);

      return () => {
        clearInterval(interval);
        document.title = `${baseName} CRM`;
        syncBadges(0);
      };
    } else {
      document.title = `${baseName} CRM`;
      syncBadges(0);
      if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator && navigator.serviceWorker.ready) {
        navigator.serviceWorker.ready.then(reg => {
          reg.getNotifications().then(notifications => {
            notifications.forEach(n => n.close());
          }).catch(() => {});
        }).catch(() => {});
      }
    }
  }, [data?.crm?.name, crmName, unreadMessages, hotCallsCount, clientHotCallWindow, clientHotCallToastsEnabled, data?.crm?.hotCallWindow, data?.logs]);

  useEffect(() => {
    if (crmName === null) return;
    const baseName = crmName || decodeURIComponent((slug || '') as string);
    let link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'manifest';
      document.head.appendChild(link);
    }
    link.crossOrigin = "anonymous";
    link.href = `/api/manifest.json?start_url=${encodeURIComponent(window.location.pathname + window.location.search)}&name=${encodeURIComponent(baseName)}&origin=${encodeURIComponent(window.location.origin)}&v=${Date.now()}`;

    document.title = `${baseName} CRM`;
    let appleTitle = document.querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-title"]');
    if (!appleTitle) {
      appleTitle = document.createElement('meta');
      appleTitle.name = 'apple-mobile-web-app-title';
      document.head.appendChild(appleTitle);
    }
    appleTitle.content = `${baseName} CRM`;
  }, [slug, crmName]);

  useEffect(() => {
    const interval = setInterval(() => {
      const n = Date.now();
      setToastQueue(q => q.filter(t => n - t.addedAt < 8000));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth <= 768) {
        setActiveTab('chat');
      }

      const params = new URLSearchParams(window.location.search);
      if (params.get('tab') === 'chat') {
        setActiveTab('chat');
        const target = params.get('target');
        if (target) {
          setTimeout(() => {
            setChatTrigger({ contact: target, did: '', ts: Date.now(), autoLookup: true });
          }, 500);

          const url = new URL(window.location.href);
          url.searchParams.delete('tab');
          url.searchParams.delete('target');
          window.history.replaceState({}, '', url.toString());
        }
      }
    }
  }, []);

  useEffect(() => {
    let isAuthed = true;

    const resetIdle = () => { idleSinceRef.current = Date.now(); };
    window.addEventListener('mousemove', resetIdle);
    window.addEventListener('keydown', resetIdle);
    window.addEventListener('click', resetIdle);
    window.addEventListener('scroll', resetIdle);

    const fetchLiveStats = async () => {
      if (!isAuthed || !slug) return;
      try {
        const pw = localStorage.getItem(`crm-pw-${slug}`) || sessionStorage.getItem(`crm-pw-${slug}`) || '';
        const agentName = localStorage.getItem(`crm-agent-${slug}`);
        const res = await crmClient.api.v1['crm-dashboard'][':slug'].live.$get(
          { param: { slug }, query: { t: Date.now().toString() } as any },
          { headers: { 'x-crm-password': pw } }
        );

        if (res.status === 401) {
          isAuthed = false;
          setData(null);
          try { if (slug) localStorage.removeItem(`crm-cache-${slug}`); } catch {}
          setRequiresAuth(true);
          setCrmRequiresPassword(true);
          try {
            const errJson = await res.json();
            if ((errJson as any).name) setCrmName((errJson as any).name);
          } catch {}
          return;
        }
        if (res.status === 403) {
          isAuthed = false;
          setData(null);
          try { if (slug) localStorage.removeItem(`crm-cache-${slug}`); } catch {}
          let errMsg = 'Access Forbidden. IP signature denied.';
          try {
            const errJson = await res.json();
            if ((errJson as any)?.error) errMsg = (errJson as any).error;
          } catch {}
          setError(errMsg);
          return;
        }
        if (res.status === 404) {
          isAuthed = false;
          setData(null);
          try { if (slug) localStorage.removeItem(`crm-cache-${slug}`); } catch {}
          setError('Dashboard not found or deactivated.');
          return;
        }
        if (!res.ok) {
          isAuthed = false;
          let errMsg = 'Dashboard unavailable';
          try {
            const errJson = await res.json();
            if ((errJson as any)?.error) errMsg = (errJson as any).error;
          } catch {}
          setError(errMsg);
          reportDataFetchError('crm', 'LiveDashboard', new Error(errMsg), { slug, status: res.status });
          return;
        }

        const json = await res.json();
        if ((json as any).error) throw new Error((json as any).error);

        const payloadData = (json as any).data;
        if (payloadData?.crm?.name) setCrmName(payloadData.crm.name);
        if (payloadData?.crm?.passwordHash) {
          setCrmRequiresPassword(true);
        } else {
          setCrmRequiresPassword(false);
        }

        if (!agentName) {
          isAuthed = false;
          setRequiresAuth(true);
          return;
        }
        if (payloadData?.logs) {
          payloadData.logs = payloadData.logs.filter((log: any) => !log.sourceIp?.startsWith('127.0.0.1'));
        }
        const idleLogoutTime = payloadData?.crm?.idleLogoutTime || 240;

        if (Date.now() - idleSinceRef.current > idleLogoutTime * 60 * 1000) {
          localStorage.removeItem(`crm-pw-${slug}`);
          localStorage.removeItem(`crm-agent-${slug}`);
          localStorage.removeItem(`crm-cache-${slug}`);
          window.location.reload();
          return;
        }

        setData(payloadData);
        try {
          if (slug) localStorage.setItem(`crm-cache-${slug}`, JSON.stringify(payloadData));
        } catch {}
      } catch (err: any) {
        setError(err.message);
        reportDataFetchError('crm', 'LiveDashboard', err, { slug });
      }
    };

    fetchLiveStats();

    return () => {
      window.removeEventListener('mousemove', resetIdle);
      window.removeEventListener('keydown', resetIdle);
      window.removeEventListener('click', resetIdle);
      window.removeEventListener('scroll', resetIdle);
    };
  }, [slug, refreshTrigger]);

  const handleAuthSubmit = async (e: any) => {
    e.preventDefault();
    setAuthError('');
    if (!authName.trim()) { setAuthError('Display Name is required'); return; }

    const effectivePassword = authInput || (slug && localStorage.getItem(`crm-pw-${slug}`)) || '';
    if (crmRequiresPassword && !effectivePassword) {
      setAuthError('Password is required');
      return;
    }

    try {
      if (slug) localStorage.setItem(`crm-agent-${slug}`, authName.trim());

      const loginRes = await crmFetch(`${API_BASE.replace(/\/$/, '')}/api/v1/crm-dashboard/${slug}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: effectivePassword, agentName: authName.trim() })
      });

      if (loginRes.status === 401) {
        setAuthError('Incorrect Password');
        return;
      } else if (loginRes.status === 403) {
        setRequiresAuth(false);
        let errMsg = 'Access Forbidden. IP signature denied.';
        try {
          const errJson = await loginRes.json();
          if ((errJson as any)?.error) errMsg = (errJson as any).error;
        } catch {}
        setError(errMsg);
        return;
      } else if (loginRes.status === 404) {
        setRequiresAuth(false);
        setError('Dashboard not found or deactivated.');
        return;
      } else if (!loginRes.ok) {
        setAuthError('Connection Failed');
        reportDataFetchError('crm', 'CrmLogin', new Error(`Login failed with status ${loginRes.status}`), { slug, status: loginRes.status });
        return;
      }

      if (effectivePassword && slug) {
        localStorage.setItem(`crm-pw-${slug}`, effectivePassword);
      }
      window.location.reload();
    } catch (err: any) {
      setAuthError('Connection Failed');
      reportDataFetchError('crm', 'CrmLogin', err, { slug });
    }
  };

  return {
    slug,
    data,
    setData,
    error,
    setError,
    requiresAuth,
    crmRequiresPassword,
    authInput,
    setAuthInput,
    authName,
    setAuthName,
    authError,
    crmName,
    page,
    setPage,
    isDrawerOpen,
    setIsDrawerOpen,
    settingsTab,
    setSettingsTab,
    clientTheme,
    setClientTheme,
    clientMaxRows,
    setClientMaxRows,
    clientHotCallWindow,
    setClientHotCallWindow,
    clientHotCallToastsEnabled,
    setClientHotCallToastsEnabled,
    clientTimezone,
    setClientTimezone,
    scrollRef,
    toastQueue,
    setToastQueue,
    activeTab,
    setActiveTab,
    unreadMessages,
    setUnreadMessages,
    cdrAuthed,
    setCdrAuthed,
    cdrPasswordInput,
    setCdrPasswordInput,
    cdrAuthError,
    setCdrAuthError,
    chatTrigger,
    setChatTrigger,
    showHeaderMobile,
    setShowHeaderMobile,
    touchStartY,
    setTouchStartY,
    showLargeQr,
    setShowLargeQr,
    contactEditorModalOpen,
    setContactEditorModalOpen,
    addressBookModalOpen,
    setAddressBookModalOpen,
    callLogsSettingsOpen,
    setCallLogsSettingsOpen,
    activeAgents,
    timeSkew,
    showAgentModal,
    setShowAgentModal,
    now,
    refreshTrigger,
    setRefreshTrigger,
    cnamRefreshTrigger,
    setCnamRefreshTrigger,
    hotCallWindow,
    hotCallsCount,
    handleAuthSubmit,
    ...webPush,
    ...chatActions
  };
}
